import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import {
  makeStyles,
  Accordion,
  ListItem,
  AccordionSummary,
  ListItemIcon,
  Tooltip,
  AccordionDetails,
  IconButton,
} from '@material-ui/core'
import ExpandIcon from '@material-ui/icons/ExpandMore'
import ClosedCaptionIcon from '@material-ui/icons/ClosedCaption'
import clsx from 'clsx'
import QueryString from 'query-string'
import PrivateIcon from '@material-ui/icons/Public'

import { EVENT_VIEW_TYPES } from '../../../redux/events/reducers'
import Formatter from '../../../utils/Formatter.js'
import Utilities from '../../../utils/Utilities'
import history from '../../../history'
import actions from '../../../redux/actions'

const noMarginNoPadding = { margin: '0 !important', padding: '0 !important' }
const useStyles = makeStyles((theme) => ({
  root: {
    ...noMarginNoPadding,
    width: 'auto',
    maxWidth: '100%',
  },
  expandedTitle: {
    backgroundColor: '#41515d !important',
    color: 'white !important',
    '& $eventID': {
      color: 'rgb(135, 206, 235)',
      '&:hover': {
        color: 'rgb(165, 236, 255)'
      }
    }
  },
  titleBar: {
    backgroundColor: 'white',
    color: 'black',
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '1.5rem'
  },
  eventID: {
    textDecoration: 'underline',
    color: 'blue',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  subTitle: {
    color: 'white'
  },
  quicklook: {
    ...noMarginNoPadding,
    'transition': 'max-height 0.5s ease',
    'maxHeight': '0px',
    'height': '100%',
    overflow: 'hidden',
    '&.isExpanded': {
      overflow: 'inherit',
      maxHeight: '690px',
      transition: 'max-height 0.5s ease',
    },
  },
  noOverflow: {
    overflow: 'hidden !important'
  },
  icon: {
    paddingLeft: 0,
    color: 'black'
  },
}))

function useInterval(callback, delay) {
  const savedCallback = useRef()

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

/**
 *
 * @param {string []} eventIDs string array of event ids to get analytics for
 * @param {boolean} condition a condition that must be met for the request to be sent
 * @param {number} interval ms time delay between requests
 * @param {function} requestEventAnalyticsAction a dispatchable action that will request the events (given eventIDs)
 */
export const useRequestEventAnalytics = (
  eventIDs,
  condition,
  interval,
  requestEventAnalyticsAction
) => {
  useInterval(() => {
    if (condition) {
      // if there isn't a request in progress request the analytics
      requestEventAnalyticsAction(eventIDs)
    }
  }, interval)
}

/**
 * Truncate encoder name 
 * @param {string} encoderName the name of the encoder
 * @param {number?} maxLength the max length of the name
 * @returns {string} the truncated name of the encoder
 * @example "OBS-2 with very long name that extends past normal encoder source bounds #123450009" => OBS-2 with...123450009
 */
const getTruncatedEncoderName = (encoderName, maxLength = 25) => {
  // name truncated with ellipsis in the center of the given name
  if (encoderName.length > maxLength) {
    const firstSection = Math.floor(maxLength / 2)
    const lastSection = maxLength - firstSection - 1
    const truncatedName = `${encoderName.substr(
      0,
      firstSection
    )}...${encoderName.substr(encoderName.length - lastSection, lastSection)}`
    return truncatedName
  }
  return encoderName
}

const SlimEvent = (props) => {
  const {
    event,
    activeEvent,
    toggleActiveEvent,
    isExpanded,
    getStatusAsClassName,
    renderQuickLook,
    encoders,
    pendingAnalyticsRequest,
    requestEventsAnalytics,
    timeZone,
    handleRecord,
    handlePause,
    handleStop
  } = props
  const [isAnimating, setIsAnimating] = useState(false)
  const clientTimeZone = moment.tz(timeZone).format('z')
  const startDate = event.startDateTime

  const dispatch = useDispatch()
  const toggleExpanded = () => {
    requestEventsAnalytics([event.eventID])
    dispatch(actions.events.setEventsExpanded({expanded: event.eventID}))
  }
  const handleExpansionChange = () => {
    toggleExpanded()
    setIsAnimating(true)
    const animationTimer = setTimeout(() => {
      setIsAnimating(false)
    }, 500)
    return () => clearTimeout(animationTimer)
  }

  let encoderName = 'Loading...'
  let encoderOnline = false
  let truncatedEncoderName = ''

  if (props.event.assignedEncoder && !props.encoders.isResolving) {
    const eventEncoder = props.encoders.data.find(encoder => encoder.encoderID === props.event.assignedEncoder)
    if (eventEncoder) {
      encoderName = eventEncoder.simpleName
      truncatedEncoderName = getTruncatedEncoderName(encoderName)
      encoderOnline = eventEncoder.online
    } else {
      encoderName = 'No Source Assigned'
    }
  }
  if (!props.event.assignedEncoder) {
    encoderName = 'No Source Assigned'
  }

  let eventDescription = Utilities.removeHtmlTags(event.description) || ''
  if (eventDescription === 'null') {
    eventDescription = ''
  }

  const classes = useStyles()
  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleExpansionChange}
      classes={{ root: classes.root }}
    >
      <ListItem>
        <AccordionSummary
          expandIcon={<ExpandIcon />}
          className={clsx(
            'titleBar',
            classes.titleBar,
            isExpanded && classes.expandedTitle
          )}
          classes={{
            root: classes.root,
            content: classes.root,
            expanded: classes.root,
            expandIcon: classes.icon,
          }}
        >
          <ListItemIcon
            className='event-icons-column'
          >
            <div className='column event-status-column'>
              <Tooltip title={<h6>{event.eventStatus}</h6>}>
                <div
                  className={
                    'event-status-container ' +
                    getStatusAsClassName(event.eventStatus)
                  }
                >
                  {Utilities.toTitleCase(event.eventStatus)}
                </div>
              </Tooltip>
              <div>
                <IconButton
                  style={
                    event.liveAiCaptions && event.liveAiCaptions.isEnabled
                      ? {}
                      : { color: 'gray', opacity: 0.5 }
                  }
                  className='closed-captioning-icon'
                  onClick={(e) => {
                    e.stopPropagation()
                    const queryString = QueryString.parse(location.search)
                    const eventID = event.eventID
                    queryString.tab = 'general'
                    queryString.eventID = eventID
                    // only select an event if there wasn't one previously
                    // otherwise it would close the selected event
                    if (!activeEvent) {
                      toggleActiveEvent(event.eventID)
                    }
                    history.push(
                      `/events?${QueryString.stringify(queryString)}`
                    )
                  }}
                >
                  <ClosedCaptionIcon />
                </IconButton>
                <span
                  title={event.private ? 'Private' : 'Public'}
                  className={clsx(
                    'public-status-icon',
                    event.private && 'private-status-icon'
                  )}
                >
                  <PrivateIcon />
                </span>
              </div>
            </div>
          </ListItemIcon>
          <div className='column event-text'>
            <div className='title'>{event.title}</div>
            <div className='description'>{eventDescription}</div>
          </div>
          <div className='column event-source-action-column'>
            {activeEvent || isExpanded ? null : (
              <div className='event-source-encoder'>
                <i
                  className={`fa fa-circle encoder-status ${
                    encoderOnline ? 'on' : 'off'
                  }`}
                ></i>
                <span title={encoderName} className='encoder-name'>
                  {truncatedEncoderName || encoderName}
                </span>
              </div>
            )}
            {/* <div className="action-buttons">
              <RecordActions
                event={event}
                encoders={encoders}
                handleRecord={(e) => {
                  e.stopPropagation();
                  handleRecord(e);
                }}
                handleStop={(e) => {
                  e.stopPropagation();
                  handleStop(e);
                }}
                handlePause={(e) => {
                  e.stopPropagation();
                  handlePause(e);
                }}
              />
            </div> */}
          </div>
          <div className='column event-date-column'>
            <span>
              <b>{`${
                Formatter.getHeadingDateFormat(startDate) + ' ' + clientTimeZone
              }`}</b>
            </span>
            <span
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!isExpanded) {
                  handleExpansionChange()
                }
                if (!activeEvent) {
                  toggleActiveEvent(event.eventID)
                }
              }}
              className={classes.eventID}
            >
              {`${event.eventID}`}
            </span>
          </div>
        </AccordionSummary>
        {isExpanded && (
          <AccordionDetails
            className={clsx(
              isExpanded && 'isExpanded',
              classes.quicklook,
              isAnimating && classes.noOverflow
            )}
          >
            {renderQuickLook()}
          </AccordionDetails>
        )}
      </ListItem>
    </Accordion>
  )
}

export default SlimEvent
